import { type FC, useMemo } from 'react';
import type { RefObject } from 'react';

import SpreadsheetV16 from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import type { SampleType, SampleTypeRow } from '@/model/SampleType.model';
import type { ValidationData } from '@/utils/sampleTypeValidation';

import {
  buildColumnsV16,
  initBulkEditSampleTypesSettingsV16,
  buildCellsCallbackV16,
  buildAfterRendererCallbackV16,
} from './BulkEditSampleTypes.utils.v16';

interface HotTableRefLike {
  hotInstance?: unknown;
}

interface BulkEditSampleTypesTableV16Props {
  data: Array<SampleTypeRow>;
  innerRef: RefObject<HotTableRefLike | null>;
  sampleTypes: Array<SampleType>;
  validationData: ValidationData | null;
  availableDetailOptions: Array<string>;
  setInvalid: (invalid: boolean) => void;
}

const BulkEditSampleTypesTableV16: FC<BulkEditSampleTypesTableV16Props> = ({
  data,
  innerRef,
  sampleTypes,
  validationData,
  availableDetailOptions,
  setInvalid,
}) => {
  const columns = useMemo(
    () =>
      buildColumnsV16({
        availableDetailOptions,
        validationData,
        sampleTypes,
        hotTableRef: innerRef,
      }),
    [availableDetailOptions, validationData, sampleTypes, innerRef]
  );

  const settings = useMemo<SpreadsheetV16Settings>(() => initBulkEditSampleTypesSettingsV16(columns), [columns]);

  const cellsCallback = useMemo(
    () => buildCellsCallbackV16({ sampleTypes, validationData }),
    [sampleTypes, validationData]
  );

  const afterRendererCallback = useMemo(() => buildAfterRendererCallbackV16(sampleTypes), [sampleTypes]);

  return (
    <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles">
      <SpreadsheetV16
        className="mt3"
        testPrefix="bulk-edit-sample-types-v16"
        data={data as unknown as Array<Record<string, unknown>>}
        innerRef={innerRef as RefObject<HotTableRef>}
        settings={settings}
        addCol={false}
        modalView={true}
        fillHandle={false}
        cells={cellsCallback}
        customAfterRenderer={afterRendererCallback}
        maxRows={data.length}
        setInvalid={setInvalid}
      />
    </div>
  );
};

export default BulkEditSampleTypesTableV16;
